// TP-Scout-Requests API client — CLUB side (M2).
//
// Isolated from the shared services/api.js (small PR surface, no risk to the
// shared axios instance) — mirrors services/clubNetworkApi.js. Paths + shapes are
// the M1 contract (dashboard/scout_request_views.py + urls.py), verified against
// the backend worktree, NEVER guessed:
//   club requests   → /dashboard/club/scout-requests/
//   rec. actions    → /dashboard/club/scout-recommendations/{id}/{action}/
//   radar           → /dashboard/club/scout-radar/
// Agent-side endpoints (feed / recommend / labels / marks) are M3 and live in a
// sibling service so the two build streams never collide.
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the club-network / agent-tender clients use.
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "object"
      ? Object.values(error.response.data)[0]
      : null) ||
    "Something went wrong. Please try again.",
});

const listPagination = (data, params) => ({
  count: data?.count ?? (Array.isArray(data?.results) ? data.results.length : 0),
  next: data?.next ?? null,
  previous: data?.previous ?? null,
  currentPage: parseInt(
    (params instanceof URLSearchParams ? params.get("page") : params?.page) || "1",
    10,
  ),
});

const isCancel = (error) => error?.name === "CanceledError" || error?.name === "AbortError";

// ── Club "our scout requests" (base /dashboard/club/scout-requests/) ──────────
export const scoutRequestService = {
  // List the club's own scout requests WITH counts (club-only). `status` filters
  // open|closed; category is filtered client-side (the list endpoint carries all).
  getRequests: async (params, signal) => {
    try {
      const res = await authApi.get("/dashboard/club/scout-requests/", { params, signal });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  getRequest: async (id, signal) => {
    try {
      const res = await authApi.get(`/dashboard/club/scout-requests/${id}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Mega-menu badge (design 4a): count of unseen recommendations on open requests.
  getBadge: async (signal) => {
    try {
      const res = await authApi.get("/dashboard/club/scout-requests/badge/", { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Forward a recommendation into an existing chat as a player card (design 5e).
  // The backend does the Stream send and scopes attribution/CTA to internal
  // channels; the sender must already be a member (403 otherwise, surfaced calmly).
  forwardRecommendation: async (recId, { channelId, text } = {}) => {
    try {
      const res = await authApi.post(
        `/dashboard/club/scout-recommendations/${recId}/forward/`,
        { channel_id: channelId, text: text || "" },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  createRequest: async (payload) => {
    try {
      const res = await authApi.post("/dashboard/club/scout-requests/", payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  updateRequest: async (id, payload) => {
    try {
      const res = await authApi.patch(`/dashboard/club/scout-requests/${id}/`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // 6c close dialog. keep_long_term=true (default) keeps the radar entries (legacy
  // state long_term); false soft-removes them. Attribution + notes survive either way.
  // DECISION-002: on keep, `targetListId` names the club radar LIST the players stay
  // in (the dialog's picker). Omitted → the server uses the closer's default My Radar.
  closeRequest: async (id, keepLongTerm = true, targetListId = null) => {
    try {
      const body = { keep_long_term: keepLongTerm };
      if (keepLongTerm && targetListId != null) body.target_list_id = targetListId;
      const res = await authApi.post(`/dashboard/club/scout-requests/${id}/close/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // The recommendations inbox for one request (paginated, club-only).
  getRecommendations: async (id, params, signal) => {
    try {
      const res = await authApi.get(
        `/dashboard/club/scout-requests/${id}/recommendations/`,
        { params, signal },
      );
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // ── per-recommendation actions (/dashboard/club/scout-recommendations/) ──
  markViewed: async (recId) => {
    try {
      const res = await authApi.post(
        `/dashboard/club/scout-recommendations/${recId}/mark_viewed/`,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  addToRadar: async (recId) => {
    try {
      const res = await authApi.post(
        `/dashboard/club/scout-recommendations/${recId}/add_to_radar/`,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  undoAddToRadar: async (recId) => {
    try {
      const res = await authApi.post(
        `/dashboard/club/scout-recommendations/${recId}/undo_add_to_radar/`,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  dismiss: async (recId) => {
    try {
      const res = await authApi.post(
        `/dashboard/club/scout-recommendations/${recId}/dismiss/`,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  undoDismiss: async (recId) => {
    try {
      const res = await authApi.post(
        `/dashboard/club/scout-recommendations/${recId}/undo_dismiss/`,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── radar (base /dashboard/club/scout-radar/) ──
  // state = active|long_term (omit for both), request = group filter, q = search.
  getRadar: async (params, signal) => {
    try {
      const res = await authApi.get("/dashboard/club/scout-radar/", { params, signal });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  setRecheck: async (entryId, dateStr) => {
    try {
      const res = await authApi.post(
        `/dashboard/club/scout-radar/${entryId}/set_recheck/`,
        { re_check_date: dateStr || null },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  toLongTerm: async (entryId) => {
    try {
      const res = await authApi.post(`/dashboard/club/scout-radar/${entryId}/to_long_term/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  reactivate: async (entryId) => {
    try {
      const res = await authApi.post(`/dashboard/club/scout-radar/${entryId}/reactivate/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  removeFromRadar: async (entryId) => {
    try {
      const res = await authApi.post(`/dashboard/club/scout-radar/${entryId}/remove/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Opens (or re-opens) the direct chat with the recommending agent; returns
  // { channel_id, channel_type }. The caller deep-links to /inbox?channel=<id>.
  contactRecommender: async (entryId) => {
    try {
      const res = await authApi.post(`/dashboard/club/scout-radar/${entryId}/contact/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  getNotes: async (entryId, signal) => {
    try {
      const res = await authApi.get(`/dashboard/club/scout-radar/${entryId}/notes/`, { signal });
      return { success: true, data: Array.isArray(res.data) ? res.data : res.data?.results || [] };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  addNote: async (entryId, text) => {
    try {
      const res = await authApi.post(`/dashboard/club/scout-radar/${entryId}/notes/`, { text });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export default scoutRequestService;
